<template>
  <main class="customer-models-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-button size="small" plain icon="el-icon-arrow-left" circle @click="goBack" style="margin-right: 4px;" />
        <span class="customer-title">{{ customerName }}</span>
        <el-input
          v-model="searchQuery"
          placeholder="搜索模型..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch" @clear="onSearch"
        />
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
        <div style="flex:1"></div>
        <el-button size="small" type="primary" icon="el-icon-plus" :disabled="!activeSupplierId" @click="openAddDialog">
          添加模型
        </el-button>
      </div>

      <el-tabs v-model="activeSupplierId" @tab-click="onTabChange">
        <el-tab-pane
          v-for="sup in suppliers"
          :key="sup.id"
          :name="String(sup.id)"
          :label="sup.name"
        />
      </el-tabs>
      <div v-if="suppliers.length === 0" class="no-supplier">
        该客户尚未绑定任何供应商，请先在「客户管理」中为客户绑定供应商。
      </div>

      <div v-if="activeSupplierId" class="desktop-only">
        <el-table :data="models" size="small" style="width: 100%">
          <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
          <el-table-column label="模型名称" min-width="240" key="model-name">
            <template slot-scope="scope">
              <div class="cm-model-cell">
                <span class="cm-model-name" :title="scope.row.name">{{ scope.row.name }}</span>
                <el-tag size="mini" class="cm-model-tag" :type="modelTypeInfo(scope.row.modelType).tagType">
                  {{ modelTypeInfo(scope.row.modelType).label }}
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="priority" label="优先级" width="70" align="center" />
          <el-table-column label="供应商" width="110">
            <template slot-scope="scope">{{ scope.row.supplierName || '-' }}</template>
          </el-table-column>
          <el-table-column v-if="hasTextModel" label="客户输入价">
            <template slot-scope="scope">
              <template v-if="scope.row.modelType === 'IMAGE' || scope.row.modelType === 'VIDEO'">-</template>
              <template v-else>¥ {{ formatPrice(scope.row.customerInputPrice) }}<span v-if="scope.row.modelType === 'ASR'" style="font-size:11px;color:var(--muted);"> /千秒</span><span v-else-if="scope.row.modelType === 'TTS'" style="font-size:11px;color:var(--muted);"> /千字符</span></template>
            </template>
          </el-table-column>
          <el-table-column label="客户输出价">
            <template slot-scope="scope">
              <span v-if="scope.row.modelType === 'EMBEDDING' || scope.row.modelType === 'RERANK' || scope.row.modelType === 'ASR' || scope.row.modelType === 'TTS'" style="color:var(--muted-2);">-</span>
              <template v-else-if="scope.row.modelType === 'VIDEO'">
                <template v-if="videoPriceList(scope.row).length">
                  <div v-for="item in videoPriceList(scope.row)" :key="item.resolution" style="font-size:12px;">
                    {{ item.resolution }} ¥ {{ formatPrice(item.outputPrice) }} /秒
                  </div>
                </template>
                <template v-else>¥ {{ formatPrice(scope.row.customerOutputPrice) }}<span style="font-size:11px;color:var(--muted);"> /秒</span></template>
              </template>
              <template v-else>¥ {{ formatPrice(scope.row.customerOutputPrice) }}<span v-if="scope.row.modelType === 'IMAGE'" style="font-size:11px;color:var(--muted);"> /张</span></template>
            </template>
          </el-table-column>
          <el-table-column v-if="hasTextModel" label="客户缓存价">
            <template slot-scope="scope">
              <template v-if="scope.row.modelType === 'IMAGE' || scope.row.modelType === 'VIDEO' || scope.row.modelType === 'EMBEDDING' || scope.row.modelType === 'RERANK' || scope.row.modelType === 'ASR' || scope.row.modelType === 'TTS'">-</template>
              <template v-else>¥ {{ formatPrice(scope.row.customerCachePrice) }}</template>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140">
            <template slot-scope="scope">
              <el-button size="small" type="text" @click="onEdit(scope.row)">修改</el-button>
              <el-button size="small" type="text" class="delete-btn" @click="onDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pagination-bar"
          layout="total, prev, pager, next"
          small
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="onPageChange"
        />
      </div>
      <div v-if="activeSupplierId" class="mobile-only list-cards">
        <div class="list-card" v-for="(row, idx) in models" :key="row.supplierModelId || idx">
          <div class="card-header">
            <div>
              <div class="card-title">{{ row.name }}
                <el-tag size="mini" :type="modelTypeInfo(row.modelType).tagType" style="margin-left:4px;">{{ modelTypeInfo(row.modelType).label }}</el-tag>
              </div>
              <div class="card-sub">优先级 {{ row.priority }} · {{ row.supplierName }}</div>
            </div>
          </div>
          <template v-if="row.modelType === 'IMAGE'">
            <div class="price-grid">
              <div class="price-cell">
                <div class="label">供应商每张价 (/张)</div>
                <div class="value output">¥ {{ formatPrice(row.supplierOutputPrice) }}</div>
              </div>
              <div class="price-cell">
                <div class="label">客户每张价 (/张)</div>
                <div class="value output" style="color:#10b981;">¥ {{ formatPrice(row.customerOutputPrice) }}</div>
              </div>
            </div>
          </template>
          <template v-else-if="row.modelType === 'VIDEO'">
            <div v-if="videoPriceList(row).length" class="price-grid">
              <div class="price-cell" style="flex:1;">
                <div class="label">{{ row.videoPrices && row.videoPrices.length ? '客户分辨率价 (/秒)' : '供应商分辨率价 (/秒)' }}</div>
                <div v-for="item in videoPriceList(row)" :key="item.resolution" class="value output" style="color:#10b981;">
                  {{ item.resolution }} ¥ {{ formatPrice(item.outputPrice) }}
                </div>
              </div>
            </div>
            <div v-else class="price-grid">
              <div class="price-cell">
                <div class="label">客户每秒价 (/秒)</div>
                <div class="value output" style="color:#10b981;">¥ {{ formatPrice(row.customerOutputPrice) }}</div>
              </div>
            </div>
          </template>
          <template v-else-if="row.modelType === 'EMBEDDING' || row.modelType === 'RERANK' || row.modelType === 'ASR' || row.modelType === 'TTS'">
            <div class="price-grid">
              <div class="price-cell">
                <div class="label">供应商{{ row.modelType === 'ASR' ? '元/千秒' : (row.modelType === 'TTS' ? '元/千字符' : '元/1K') }}输入</div>
                <div class="value input">¥ {{ formatPrice(row.supplierInputPrice) }}</div>
              </div>
              <div class="price-cell">
                <div class="label">客户{{ row.modelType === 'ASR' ? '元/千秒' : (row.modelType === 'TTS' ? '元/千字符' : '元/1K') }}输入</div>
                <div class="value input" style="color:#10b981;">¥ {{ formatPrice(row.customerInputPrice) }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="price-grid">
              <div class="price-cell">
                <div class="label">客户输入</div>
                <div class="value input" style="color:#10b981;">¥ {{ formatPrice(row.customerInputPrice) }}</div>
              </div>
              <div class="price-cell">
                <div class="label">客户输出</div>
                <div class="value output" style="color:#10b981;">¥ {{ formatPrice(row.customerOutputPrice) }}</div>
              </div>
            </div>
          </template>
          <div class="card-actions">
            <el-button size="small" type="text" @click="onEdit(row)">修改</el-button>
            <el-button size="small" type="text" class="delete-btn" @click="onDelete(row)">删除</el-button>
          </div>
        </div>
        <el-pagination
          class="pagination-bar"
          layout="total, prev, pager, next"
          small
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="onPageChange"
        />
      </div>
    </section>

    <el-dialog title="添加模型" :visible.sync="addDialogVisible" width="560px" :close-on-click-modal="false" top="16vh">
      <div style="margin-bottom:16px;font-size:13px;color:var(--muted);">
        <i class="el-icon-s-platform" style="margin-right:4px;"></i>
        {{ customerName }} — {{ activeSupplierName }}
      </div>
      <el-form ref="addForm" :model="addForm" label-width="0" size="small">
        <el-form-item prop="supplierModelId">
          <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">选择模型（该供应商下未添加的）</div>
          <el-select
            v-model="addForm.supplierModelId"
            filterable
            placeholder="搜索并选择模型"
            style="width:100%"
            :loading="addableLoading"
            @change="onAddableChange"
          >
            <el-option
              v-for="m in addableModels"
              :key="m.supplierModelId"
              :label="m.name"
              :value="m.supplierModelId"
            >
              <span>{{ m.name }}</span>
              <span style="float:right;color:var(--muted-2);font-size:12px;">
                <template v-if="m.modelType === 'IMAGE'">¥{{ formatPrice(m.supplierOutputPrice) }} /张</template>
                <template v-else-if="m.modelType === 'VIDEO'">¥{{ formatPrice(m.supplierOutputPrice) }} /秒</template>
                <template v-else-if="m.modelType === 'ASR'">¥{{ formatPrice(m.supplierInputPrice) }} /千秒</template>
                <template v-else-if="m.modelType === 'TTS'">¥{{ formatPrice(m.supplierInputPrice) }} /千字符</template>
                <template v-else>¥{{ formatPrice(m.supplierInputPrice) }} /1K</template>
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <div style="display:flex;gap:12px;align-items:center;margin-bottom:12px;">
          <div style="flex:1;font-size:12px;color:var(--muted);">优先级（0 最优，数字越大优先级越低，优先走数字小的）</div>
          <el-input-number v-model="addForm.priority" :min="0" size="small" style="width:130px;" />
        </div>
        <div v-if="selectedAddable && selectedAddable.modelType === 'IMAGE'" style="display:flex;gap:12px;">
          <el-form-item style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户每张价格（元/张）</div>
            <el-input v-model="addForm.outputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/张</span></template>
            </el-input>
          </el-form-item>
        </div>
        <div v-else-if="selectedAddable && selectedAddable.modelType === 'VIDEO'" style="display:flex;flex-direction:column;gap:10px;">
          <el-form-item>
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户基础每秒价（未配置分辨率价格时兜底）</div>
            <el-input v-model="addForm.outputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/秒</span></template>
            </el-input>
          </el-form-item>
          <div style="display:flex;gap:12px;">
            <el-form-item v-for="resolution in videoResolutions" :key="resolution" style="flex:1;">
              <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">{{ resolution }} 客户价</div>
              <el-input v-model="addForm.videoPrices[resolution]" placeholder="留空则用基础价">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
                <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/秒</span></template>
              </el-input>
            </el-form-item>
          </div>
        </div>
        <div v-else-if="selectedAddable && (selectedAddable.modelType === 'EMBEDDING' || selectedAddable.modelType === 'RERANK' || selectedAddable.modelType === 'ASR' || selectedAddable.modelType === 'TTS')" style="display:flex;gap:12px;">
          <el-form-item style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户输入价</div>
            <el-input v-model="addForm.inputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">{{ selectedAddable.modelType === 'ASR' ? '/千秒' : (selectedAddable.modelType === 'TTS' ? '/千字符' : '/1K tokens') }}</span></template>
            </el-input>
          </el-form-item>
        </div>
        <div v-else style="display:flex;gap:12px;">
          <el-form-item style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户输入价</div>
            <el-input v-model="addForm.inputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
            </el-input>
          </el-form-item>
          <el-form-item style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户输出价</div>
            <el-input v-model="addForm.outputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
            </el-input>
          </el-form-item>
          <el-form-item style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户缓存价</div>
            <el-input v-model="addForm.cachePrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
            </el-input>
          </el-form-item>
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="addDialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="submitAdd" size="small" :loading="submitting">确定添加</el-button>
      </span>
    </el-dialog>

    <el-dialog title="修改模型价格" :visible.sync="dialogVisible" width="620px" :close-on-click-modal="false" top="18vh">
      <div style="margin-bottom:20px;font-size:13px;color:var(--muted);">
        <i class="el-icon-s-platform" style="margin-right:4px;"></i>
        {{ customerName }} — {{ editingName }}
      </div>
      <div style="display:flex;gap:12px;align-items:center;margin-bottom:12px;">
        <div style="flex:1;font-size:12px;color:var(--muted);">优先级（0 最优，数字越大优先级越低，优先走数字小的）</div>
        <el-input-number v-model="form.priority" :min="0" size="small" style="width:130px;" />
      </div>
      <el-form ref="form" :model="form" label-width="0" size="small">
        <div v-if="editingModelType === 'IMAGE'" style="display:flex;gap:12px;">
          <el-form-item prop="outputPrice" style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户每张价格（元/张）</div>
            <el-input v-model="form.outputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/张</span></template>
            </el-input>
            <div style="margin-top:4px;font-size:11px;color:var(--muted-2);">供应商每张价: ¥ {{ formatPrice(form.supplierOutputPrice) }}</div>
          </el-form-item>
        </div>
        <div v-else-if="editingModelType === 'VIDEO'" style="display:flex;flex-direction:column;gap:10px;">
          <el-form-item prop="outputPrice">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户基础每秒价（未配置分辨率价格时兜底）</div>
            <el-input v-model="form.outputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/秒</span></template>
            </el-input>
            <div style="margin-top:4px;font-size:11px;color:var(--muted-2);">供应商基础每秒价: ¥ {{ formatPrice(form.supplierOutputPrice) }}</div>
          </el-form-item>
          <div style="display:flex;gap:12px;">
            <el-form-item v-for="resolution in videoResolutions" :key="resolution" style="flex:1;">
              <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">{{ resolution }} 客户价</div>
              <el-input v-model="videoPrices[resolution]" placeholder="留空则用基础价">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
                <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/秒</span></template>
              </el-input>
            </el-form-item>
          </div>
        </div>
        <div v-else-if="editingModelType === 'EMBEDDING' || editingModelType === 'RERANK' || editingModelType === 'ASR' || editingModelType === 'TTS'" style="display:flex;gap:12px;">
          <el-form-item prop="inputPrice" style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户输入价</div>
            <el-input v-model="form.inputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">{{ editingModelType === 'ASR' ? '/千秒' : (editingModelType === 'TTS' ? '/千字符' : '/1K tokens') }}</span></template>
            </el-input>
            <div style="margin-top:4px;font-size:11px;color:var(--muted-2);">供应商价: ¥ {{ formatPrice(form.supplierInputPrice) }}</div>
          </el-form-item>
        </div>
        <div v-else style="display:flex;gap:12px;">
          <el-form-item prop="inputPrice" style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户输入价</div>
            <el-input v-model="form.inputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/1K tokens</span></template>
            </el-input>
            <div style="margin-top:4px;font-size:11px;color:var(--muted-2);">供应商价: ¥ {{ formatPrice(form.supplierInputPrice) }}</div>
          </el-form-item>
          <el-form-item prop="outputPrice" style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户输出价</div>
            <el-input v-model="form.outputPrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/1K tokens</span></template>
            </el-input>
            <div style="margin-top:4px;font-size:11px;color:var(--muted-2);">供应商价: ¥ {{ formatPrice(form.supplierOutputPrice) }}</div>
          </el-form-item>
          <el-form-item prop="cachePrice" style="flex:1;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">客户缓存价</div>
            <el-input v-model="form.cachePrice" placeholder="0.00">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/1K tokens</span></template>
            </el-input>
            <div style="margin-top:4px;font-size:11px;color:var(--muted-2);">供应商价: ¥ {{ formatPrice(form.supplierCachePrice) }}</div>
          </el-form-item>
        </div>
      </el-form>
      <template v-if="editingModelType === 'TEXT'">
        <el-divider style="margin: 16px 0;" />
        <div style="margin-bottom:8px;font-size:13px;color:var(--text);font-weight:700;">
          客户分时段价格
          <span style="font-weight:400;color:var(--muted);font-size:12px;margin-left:6px;">未配置时段或未命中的时段按上方客户价计费，最多 5 段，支持跨午夜</span>
        </div>
        <div v-for="(slot, index) in timePrices" :key="index" style="padding:10px;border:1px solid var(--line-soft);border-radius:8px;margin-bottom:10px;background:rgba(255,255,255,0.03);">
          <div style="display:flex;gap:6px;align-items:center;">
            <el-time-picker v-model="slot.startTime" value-format="HH:mm" format="HH:mm" placeholder="开始" style="width:120px;"></el-time-picker>
            <span style="color:var(--muted-2);">至</span>
            <el-time-picker v-model="slot.endTime" value-format="HH:mm" format="HH:mm" placeholder="结束" style="width:120px;"></el-time-picker>
            <el-button type="text" size="small" class="delete-btn" style="margin-left:auto;" @click="removeTimePrice(index)">删除</el-button>
          </div>
          <div style="display:flex;gap:6px;margin-top:8px;">
            <el-input v-model="slot.inputPrice" placeholder="客户输入价" size="small">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
            </el-input>
            <el-input v-model="slot.outputPrice" placeholder="客户输出价" size="small">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
            </el-input>
            <el-input v-model="slot.cachePrice" placeholder="客户缓存价" size="small">
              <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
            </el-input>
          </div>
        </div>
        <el-button v-if="timePrices.length < 5" type="primary" plain size="mini" icon="el-icon-plus" @click="addTimePrice">添加时段</el-button>
      </template>
      <span slot="footer">
        <el-button @click="dialogVisible = false" size="small">取消</el-button>
        <el-button type="primary" @click="submitForm" size="small" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>
  </main>
</template>

<script>
import { modelTypeInfo as mtInfo } from '../utils/format'
export default {
  name: 'CustomerModelsPage',
  data() {
    return {
      suppliers: [],
      activeSupplierId: null,
      models: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: '',
      addDialogVisible: false,
      addableLoading: false,
      addableModels: [],
      addForm: {
        supplierModelId: null,
        priority: 1,
        inputPrice: '',
        outputPrice: '',
        cachePrice: '',
        videoPrices: { '480P': '', '720P': '', '1080P': '' }
      },
      dialogVisible: false,
      submitting: false,
      editingName: '',
      editingModelType: 'TEXT',
      form: {
        supplierModelId: null,
        priority: 1,
        inputPrice: '',
        outputPrice: '',
        cachePrice: '',
        supplierInputPrice: 0,
        supplierOutputPrice: 0,
        supplierCachePrice: 0
      },
      timePrices: [],
      videoResolutions: ['480P', '720P', '1080P'],
      videoPrices: { '480P': '', '720P': '', '1080P': '' }
    }
  },
  computed: {
    customerId() {
      return this.$route.params.id
    },
    customerName() {
      return this.$route.query.name || ''
    },
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    hasTextModel() {
      return this.models.some(item => ['TEXT', 'EMBEDDING', 'RERANK', 'ASR', 'TTS'].includes(item.modelType))
    },
    activeSupplierName() {
      const sup = this.suppliers.find(s => String(s.id) === this.activeSupplierId)
      return sup ? sup.name : ''
    },
    selectedAddable() {
      return this.addableModels.find(m => m.supplierModelId === this.addForm.supplierModelId) || null
    }
  },
  mounted() {
    this.fetchSuppliers()
  },
  methods: {
    modelTypeInfo(type) {
      return mtInfo(type)
    },
    videoPriceList(row) {
      if (row.videoPrices && row.videoPrices.length) return row.videoPrices
      if (row.supplierVideoPrices && row.supplierVideoPrices.length) return row.supplierVideoPrices
      return []
    },
    formatPrice(value) {
      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
      }).format(value || 0)
    },
    onTabChange() {
      this.currentPage = 1
      this.fetchModels()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchModels()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchModels()
    },
    goBack() {
      this.$router.push('/customers')
    },
    async fetchSuppliers() {
      try {
        const res = await this.$http.get('/admin/customers/' + this.customerId + '/models/suppliers')
        this.suppliers = res || []
        if (this.suppliers.length > 0) {
          // 默认选中第一个供应商；若当前 activeSupplierId 不再存在于列表则重置到第一个
          const currentValid = this.suppliers.some(s => String(s.id) === this.activeSupplierId)
          if (!currentValid) {
            this.activeSupplierId = String(this.suppliers[0].id)
          }
          this.fetchModels()
        } else {
          this.activeSupplierId = null
        }
      } catch {
        this.suppliers = []
        this.$message.error('加载供应商列表失败')
      }
    },
    async fetchModels() {
      if (!this.activeSupplierId) return
      try {
        const res = await this.$http.get('/admin/customers/' + this.customerId + '/models', {
          params: {
            supplierId: this.activeSupplierId,
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.models = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载模型列表失败')
      }
    },
    async openAddDialog() {
      this.addForm = {
        supplierModelId: null,
        priority: this.nextPriority(),
        inputPrice: '',
        outputPrice: '',
        cachePrice: '',
        videoPrices: this.emptyVideoPrices()
      }
      this.addDialogVisible = true
      await this.fetchAddableModels()
    },
    async fetchAddableModels() {
      this.addableLoading = true
      try {
        const res = await this.$http.get('/admin/customers/' + this.customerId + '/models/addable', {
          params: { supplierId: this.activeSupplierId, page: 1, pageSize: 100 }
        })
        this.addableModels = res.records || []
      } catch {
        this.addableModels = []
        this.$message.error('加载可选模型失败')
      } finally {
        this.addableLoading = false
      }
    },
    async onAddableChange(supplierModelId) {
      const m = this.addableModels.find(x => x.supplierModelId === supplierModelId)
      if (!m) return
      this.addForm.inputPrice = String(m.supplierInputPrice ?? '')
      this.addForm.outputPrice = String(m.supplierOutputPrice ?? '')
      this.addForm.cachePrice = String(m.supplierCachePrice ?? '')
      this.addForm.videoPrices = this.emptyVideoPrices()
      if (m.modelType === 'VIDEO') {
        await this.fetchSupplierVideoPrices(supplierModelId)
      }
    },
    emptyVideoPrices() {
      return { '480P': '', '720P': '', '1080P': '' }
    },
    async fetchSupplierVideoPrices(supplierModelId) {
      try {
        const res = await this.$http.get('/admin/suppliers/' + this.activeSupplierId +
          '/models/' + supplierModelId + '/video-prices')
        const prices = this.emptyVideoPrices()
        for (const item of (res || [])) {
          prices[item.resolution] = String(item.outputPrice ?? '')
        }
        this.addForm.videoPrices = prices
      } catch {
        this.addForm.videoPrices = this.emptyVideoPrices()
      }
    },
    async fetchCustomerVideoPrices(supplierModelId) {
      try {
        const res = await this.$http.get('/admin/customers/' + this.customerId +
          '/models/' + supplierModelId + '/video-prices')
        const prices = this.emptyVideoPrices()
        for (const item of (res || [])) {
          prices[item.resolution] = String(item.outputPrice ?? '')
        }
        this.videoPrices = prices
      } catch {
        this.videoPrices = this.emptyVideoPrices()
      }
    },
    buildVideoPricesPayload(source) {
      return this.videoResolutions
        .filter(resolution => source[resolution] !== '' && source[resolution] != null)
        .map(resolution => ({
          resolution,
          outputPrice: parseFloat(source[resolution]) || 0
        }))
    },
    nextPriority() {
      const max = this.models.reduce((acc, m) => Math.max(acc, Number(m.priority) || 1), 0)
      return max + 1
    },
    submitAdd() {
      if (!this.addForm.supplierModelId) {
        this.$message.error('请选择模型')
        return
      }
      const sel = this.selectedAddable
      const isInputOnly = sel && (sel.modelType === 'EMBEDDING' || sel.modelType === 'RERANK' || sel.modelType === 'ASR' || sel.modelType === 'TTS')
      const isOutputOnly = sel && (sel.modelType === 'IMAGE' || sel.modelType === 'VIDEO')
      const payload = {
        supplierId: this.activeSupplierId,
        supplierModelId: this.addForm.supplierModelId,
        priority: Number(this.addForm.priority) || 1,
        inputPrice: isInputOnly ? (parseFloat(this.addForm.inputPrice) || 0) : (isOutputOnly ? 0 : (parseFloat(this.addForm.inputPrice) || 0)),
        outputPrice: isInputOnly ? 0 : (parseFloat(this.addForm.outputPrice) || 0),
        cachePrice: isInputOnly || isOutputOnly ? 0 : (parseFloat(this.addForm.cachePrice) || 0)
      }
      this.submitting = true
      this.$http.post('/admin/customers/' + this.customerId + '/models', payload)
        .then(async () => {
          if (sel && sel.modelType === 'VIDEO') {
            await this.$http.put('/admin/customers/' + this.customerId + '/models/' +
              this.addForm.supplierModelId + '/video-prices',
              this.buildVideoPricesPayload(this.addForm.videoPrices))
          }
        })
        .then(() => {
          this.$message.success('添加成功')
          this.addDialogVisible = false
          this.fetchSuppliers()
          this.fetchModels()
        })
        .catch(() => {
          this.$message.error('添加失败，请检查选中的模型是否已添加或价格是否有效')
        })
        .finally(() => {
          this.submitting = false
        })
    },
    onEdit(row) {
      this.editingName = row.name
      this.editingModelType = ['TEXT', 'IMAGE', 'VIDEO', 'EMBEDDING', 'RERANK', 'ASR', 'TTS'].indexOf(row.modelType) >= 0 ? row.modelType : 'TEXT'
      this.form = {
        supplierModelId: row.supplierModelId,
        priority: Number(row.priority) || 1,
        inputPrice: row.customerInputPrice != null ? String(row.customerInputPrice) : '',
        outputPrice: row.customerOutputPrice != null ? String(row.customerOutputPrice) : '',
        cachePrice: row.customerCachePrice != null ? String(row.customerCachePrice) : '',
        supplierInputPrice: row.supplierInputPrice,
        supplierOutputPrice: row.supplierOutputPrice,
        supplierCachePrice: row.supplierCachePrice
      }
      this.timePrices = []
      this.videoPrices = this.emptyVideoPrices()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
      this.fetchTimePrices(row.supplierModelId)
      if (row.modelType === 'VIDEO') {
        this.fetchCustomerVideoPrices(row.supplierModelId)
      }
    },
    async fetchTimePrices(supplierModelId) {
      try {
        const res = await this.$http.get('/admin/customers/' + this.customerId + '/models/' + supplierModelId + '/time-prices')
        this.timePrices = (res || []).map(item => ({
          startTime: this.minutesToHHmm(item.startMinute),
          endTime: this.minutesToHHmm(item.endMinute, true),
          inputPrice: String(item.inputPrice ?? ''),
          outputPrice: String(item.outputPrice ?? ''),
          cachePrice: String(item.cachePrice ?? '')
        }))
      } catch {
        this.timePrices = []
      }
    },
    onDelete(row) {
      this.$confirm('确定删除模型「' + row.name + '」？删除后该客户将无法使用该供应商的此模型。', '删除确认', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$http.delete('/admin/customers/' + this.customerId + '/models/' + row.supplierModelId)
          .then(() => {
            this.$message.success('删除成功')
            this.fetchModels()
          })
          .catch(() => this.$message.error('删除失败'))
      }).catch(() => {})
    },
    addTimePrice() {
      if (this.timePrices.length >= 5) return
      this.timePrices.push({ startTime: '', endTime: '', inputPrice: '', outputPrice: '', cachePrice: '' })
    },
    removeTimePrice(index) {
      this.timePrices.splice(index, 1)
    },
    minutesToHHmm(minutes, isEnd = false) {
      if (minutes == null) return ''
      const total = Number(minutes)
      if (isEnd && total === 1440) return '00:00'
      if (total < 0 || total >= 24 * 60) return ''
      const h = String(Math.floor(total / 60)).padStart(2, '0')
      const m = String(total % 60).padStart(2, '0')
      return h + ':' + m
    },
    hhmmToMinutes(v, isEnd = false) {
      if (!v) return null
      const m = v.split(':')
      let total = parseInt(m[0], 10) * 60 + parseInt(m[1], 10)
      if (isEnd && total === 0) return 1440
      return total
    },
    validateTimeSlots() {
      for (const slot of this.timePrices) {
        if (!slot.startTime || !slot.endTime) {
          this.$message.error('时段需同时设置开始与结束时间')
          return false
        }
        if (!/^\d+(\.\d+)?$/.test(slot.inputPrice || '') || !/^\d+(\.\d+)?$/.test(slot.outputPrice || '') || !/^\d+(\.\d+)?$/.test(slot.cachePrice || '')) {
          this.$message.error('时段价格请输入有效数字')
          return false
        }
      }
      const parsed = this.timePrices.map(s => ({
        start: this.hhmmToMinutes(s.startTime),
        end: this.hhmmToMinutes(s.endTime, true)
      }))
      for (const p of parsed) {
        if (p.start == null || p.start < 0 || p.start >= 1440 || p.end == null || p.end <= 0 || p.end > 1440 || p.start === p.end) {
          this.$message.error('时段时间不合法：开始 00:00-23:59，结束 00:01-24:00')
          return false
        }
      }
      for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
          if (this.slotsOverlap(parsed[i], parsed[j])) {
            this.$message.error('时段存在重叠，请检查后重试')
            return false
          }
        }
      }
      return true
    },
    inSlot(point, start, end) {
      return end > start ? (point >= start && point < end) : (point >= start || point < end)
    },
    slotsOverlap(a, b) {
      return this.inSlot(a.start, b.start, b.end) || this.inSlot(b.start, a.start, a.end)
    },
    submitForm() {
      if (!this.validateTimeSlots()) return
      this.submitting = true
      const isInputOnly = this.editingModelType === 'EMBEDDING' || this.editingModelType === 'RERANK' || this.editingModelType === 'ASR' || this.editingModelType === 'TTS'
      const isOutputOnly = this.editingModelType === 'IMAGE' || this.editingModelType === 'VIDEO'
      const payload = {
        priority: Number(this.form.priority) || 1,
        inputPrice: isOutputOnly ? 0 : (parseFloat(this.form.inputPrice) || 0),
        outputPrice: isInputOnly ? 0 : (parseFloat(this.form.outputPrice) || 0),
        cachePrice: isOutputOnly || isInputOnly ? 0 : (parseFloat(this.form.cachePrice) || 0)
      }
      const timePricesPayload = isOutputOnly || isInputOnly
        ? []
        : this.timePrices.map(slot => ({
            startMinute: this.hhmmToMinutes(slot.startTime),
            endMinute: this.hhmmToMinutes(slot.endTime, true),
            inputPrice: parseFloat(slot.inputPrice) || 0,
            outputPrice: parseFloat(slot.outputPrice) || 0,
            cachePrice: parseFloat(slot.cachePrice) || 0
          }))
      this.$http.put('/admin/customers/' + this.customerId + '/models/' + this.form.supplierModelId, payload)
        .then(() => this.$http.put('/admin/customers/' + this.customerId + '/models/' + this.form.supplierModelId + '/time-prices', timePricesPayload))
        .then(async () => {
          if (this.editingModelType === 'VIDEO') {
            await this.$http.put('/admin/customers/' + this.customerId + '/models/' +
              this.form.supplierModelId + '/video-prices',
              this.buildVideoPricesPayload(this.videoPrices))
          }
        })
        .then(() => {
          this.$message.success('修改成功')
          this.dialogVisible = false
          this.fetchModels()
        })
        .catch(() => {
          this.$message.error('操作失败')
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped>
.customer-models-page {
  padding: 18px 24px 76px;
}

.customer-models-page ::v-deep .el-tabs__header {
  border-bottom: 1px solid rgba(133, 164, 255, 0.15);
  margin-bottom: 20px;
}

.customer-models-page ::v-deep .el-tabs__item {
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 800;
  height: 44px;
  line-height: 44px;
  letter-spacing: 0.02em;
}

.customer-models-page ::v-deep .el-tabs__item:hover {
  color: var(--text);
}

.customer-models-page ::v-deep .el-tabs__item.is-active {
  color: var(--text);
}

.customer-models-page ::v-deep .el-tabs__active-bar {
  background: #55f0b3;
  height: 2.5px;
  border-radius: 2px;
}

.customer-models-page ::v-deep .el-tabs__nav-wrap::after {
  background: transparent;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.customer-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  margin-right: 6px;
}

.search-input {
  width: 200px;
}

.no-supplier {
  padding: 20px;
  font-size: 13px;
  color: var(--muted-2);
}

.list-cards {
  display: grid;
  gap: 14px;
}

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .customer-models-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
    overflow-x: visible;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
}
.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease;
}
.list-card:active {
  transform: translateY(-1px);
  box-shadow: 0 12px 36px rgba(0,0,0,.3);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.card-sub {
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin-top: 2px;
}
.price-grid {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.price-cell {
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 8px 6px;
  border-radius: 8px;
  background: rgba(255,255,255,.04);
}
.price-cell .label {
  font-size: 10px;
  color: rgba(255,255,255,.35);
  letter-spacing: .02em;
}
.price-cell .value {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin-top: 2px;
}
.price-cell .value.input { color: #32c8f5; }
.price-cell .value.output { color: #f4b74f; }
.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
}
.card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 10px;
  height: auto;
}
.delete-btn {
  color: #ff93a4;
}

.delete-btn:hover {
  color: #ffb6c1;
}

.cm-model-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cm-model-name {
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.cm-model-tag {
  flex: none;
}
</style>